import {useMemo, useRef, useState} from 'react';
import '../../../globals/style/GlobalStyle.css';
import './SearchOverview.css';
import {getSearchResults} from '../../../utility/FetchAPI';
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router-dom';
import {showPdfInNewTab} from '../../../utility/Helper';
import {closeSearch} from '../../../utility/DialogHelper';
import {useLanguageMappings} from '../../../globals/LanguageMappingsContext';
import {debounce} from "lodash";


// Utility function to highlight search term
const highlightText = (text, searchTerm) => {
    if (!text) return ''; // Return an empty string if text is null or undefined
    if (!searchTerm) return text;
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    return text.replace(regex, '<span class="highlight">$1</span>');
};

// Utility function to get the relevant portion of text
const getRelevantText = (text = '', searchTerm = '') => {
    if (!text.trim()) return '';

    const words = text.split(/\s+/); // Split by spaces (handles multiple spaces too)
    const lowerText = text.toLowerCase();
    const lowerSearchTerm = searchTerm.toLowerCase();

    // Find the index of the search term
    const searchIndex = lowerText.indexOf(lowerSearchTerm);

    if (searchIndex !== -1) {
        // Find the start and end word indices for the relevant snippet
        const wordStartIndex = lowerText.slice(0, searchIndex).split(/\s+/).length - 1;
        const wordEndIndex = wordStartIndex + lowerSearchTerm.split(/\s+/).length;

        // Include up to 5 words before and after the search term
        const startIdx = Math.max(0, wordStartIndex - 5);
        const endIdx = Math.min(words.length, wordEndIndex + 5);

        return words.slice(startIdx, endIdx).join(' ') + '...';
    }

    // If the search term is not found, return the first 10 words
    return words.slice(0, 10).join(' ') + '...';
};


export const SearchOverview = () => {
    const languageMappings = useLanguageMappings();
    const [searchText, setSearchText] = useState('');
    const [searchResult, setSearchResult] = useState();
    const [abortController, setAbortController] = useState(null);
    const {t, i18n} = useTranslation();
    const mapping = languageMappings[i18n.language] || languageMappings['en'];
    let navigate = useNavigate();
    const location = useLocation();
    const abortControllerRef = useRef(null);
    const latestRequestIdRef = useRef(0);

    const search = async (text) => {
        if (abortController) {
            abortController.abort(); // Cancel the previous request
        }
        const controller = new AbortController();
        setAbortController(controller);
        // setSearchText(text);
        getSearchResults(text).then((results) => {
            setSearchResult(results.data);
        });
    };

    // ✅ Memoized debounce function to prevent re-creating on every render
    const debouncedSearch = useMemo(() => debounce(async (text, requestId) => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            const controller = new AbortController();
            abortControllerRef.current = controller;

            try {
                const response = await getSearchResults(text, {signal: controller.signal});
                if (requestId === latestRequestIdRef.current) {
                    setSearchResult(response.data); // ✅ Ensure only latest response is set
                }
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Search request failed:', error);
                }
            }
        }, 900), // ✅ 300ms debounce to wait for user to stop typing
        []);


    const handleSearchChange = (e) => {
        const text = e.target.value;
        setSearchText(text);
        latestRequestIdRef.current += 1; // ✅ Increment request ID for each new input
        debouncedSearch(text, latestRequestIdRef.current);
    };

    const path = (sheet) => {
        if (sheet.pdfDe == null && sheet.pdfEn == null) {
            return `/sheet/${sheet.sheetNumber.replaceAll(' ', '_')}/${i18n.language}`;
        }
        return location.pathname;
    };

    const searchButton = () => {
        getSearchResults(searchText).then((results) => {
            setSearchResult(results.data);
        });
    };

    const closeAndResetSearch = () => {
        closeSearch();
        setSearchText('');
        setSearchResult(null);
    };

    const selectSheetItemResult = (sheet) => {

        if (sheet.pdfDe === null || sheet.pdfDe === '' || sheet.pdfEn === null || sheet.pdfEn === '') {
            setSearchText('');
            setSearchResult(null);
        }
        showPdfInNewTab({sheet, language: i18n.language, navigate, mapping, newWindow: true, t, i18n});
    };

    return (<div
        id='searchContainer'
        className='notVisible searchContainerStyle foreground'
    >
        <div className='inputSearch'>
            <wb-input-control>
                <wb-input>
                    <label>{t('SearchLabel')}</label>
                    <input
                        onChange={handleSearchChange}
                        className='searchInput'
                        type='text'
                        value={searchText}
                    />
                    <wb-input-action>
                        <button
                            className='wb-round-button wb-round-button--small'
                            type='button'
                            onClick={() => {
                                setSearchText('');
                                setSearchResult(null);
                            }}
                        >
                            <wb-icon className='wb-round-button__icon' name='close'/>
                            Clear input
                        </button>
                    </wb-input-action>
                </wb-input>
            </wb-input-control>
        </div>
        <div className='searchButton'>
            <button
                type='button'
                aria-label='Search Button'
                className='wb-button wb-button--primary wb-button--large wb-button--icon-only'
                onClick={searchButton}
            >
                <wb-icon
                    className='wb-button__icon'
                    aria-hidden='true'
                    name='search'
                ></wb-icon>
                Search
            </button>
        </div>
        <wb-button-control
            variant='tertiary'
            className='backButton wb-type-button-secondary'
            onClick={closeAndResetSearch}
        >
            <button type='button'>{t('BackToOverview')}</button>
        </wb-button-control>
        <div className='textWhite wb-type-label textSpace'>
            {searchText !== '' && (
                <p>{`${t('SearchResultPart1')} ${searchResult?.length} ${t('SearchResultPart2')}`}</p>)}
        </div>
        <div className='cardContainer'>
            {searchResult?.map((productSheet) => {
                const langKey = `additionalInfo${i18n.language.charAt(0).toUpperCase() + i18n.language.slice(1)}`;
                const rawText = productSheet[langKey] || '';
                const relevantText = getRelevantText(rawText, searchText);
                const highlightedText = highlightText(relevantText, searchText);
                console.log(highlightedText);

                return (<div
                    className='wb-padding-s searchCard'
                    onClick={() => selectSheetItemResult(productSheet)}
                >
                    <wb-card interactive variant='dark-grey' className="hydrated" style={{border: "none"}}>
                        <div className="totalcontentRow">
                            <div className="left-column">
                                <div className="text wb-type-hint firstColumnHeader">{t("Sheet")}</div>
                                <div
                                    className="wb-type-heading-m firstColumnText">
                                    {productSheet?.sheetNumber}
                                </div>
                            </div>
                            <div className="right-column">
                                <div className="cardbox">
                                    <div className="extrabox">
                                        <div className="wb-type-hint secondColumnHeader">
                                            {t("Description")}</div>
                                        <div className="wb-type-label secondColumnText"
                                             dangerouslySetInnerHTML={{
                                                 __html: (() => {
                                                     switch (i18n.language) {
                                                         case "de":
                                                             return highlightText(productSheet?.titleDe || '', searchText);
                                                         case "pt":
                                                             return highlightText(productSheet?.titlePt || '', searchText);
                                                         default:
                                                             return highlightText(productSheet?.titleEn || '', searchText); // Default to English
                                                     }
                                                 })(),
                                             }}
                                        ></div>
                                        <div className="text wb-type-label"
                                             dangerouslySetInnerHTML={{__html: highlightedText,}}></div>
                                    </div>
                                </div>
                            </div>
                            <wb-icon name='chevron-right' class="cardIcon"></wb-icon>
                        </div>
                    </wb-card>
                </div>);
            })}
        </div>
    </div>);
};
